import React, { FC, useState, useRef, useEffect } from "react";
import styles from "./SearchFormSelector.module.scss";
import DownwardArrow from "@/components/icons/DownwardArrow";

interface Option {
  label: string;
  value: string;
  symbol?: string;
}

interface CustomSelectProps {
  options: Option[];
  onChange: (selectedValue: string) => void;
  value?: string;
  currency?: boolean;
  nationality?: boolean;
  dropdownIsOpen: boolean;
  showOption?: boolean;
  className?: string;
  hasArrow?: boolean;
  prefix?: boolean;
  hasFullWidth?: boolean;
  isCountry?: boolean;
  customBg?: string;
  hasSearch?: boolean;
}

const CustomSelectSearch: FC<CustomSelectProps> = ({
  options,
  onChange,
  value,
  dropdownIsOpen,
  currency = false,
  nationality = false,
  showOption,
  className,
  hasArrow = false,
  prefix = false,
  isCountry = false,
  customBg,
  hasSearch = false,
}) => {

  const selectMenuRef = useRef<HTMLDivElement>(null);
  const [hoveredOption, setHoveredOption] = useState<string | undefined>(value);
  const optionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setHoveredOption(value);
  }, [value]);

  useEffect(() => {
    if (dropdownIsOpen) {
      inputRef.current?.focus();
    }
    if (dropdownIsOpen && value) {
      const optionElement = optionRefs.current.get(value);
      if (optionElement) {
        optionElement.scrollIntoView({
          behavior: "instant",
          block: "nearest",
        });
      }
    }
  }, [dropdownIsOpen, value]);

  const handleSelect = (option: Option) => {
    setHoveredOption(option.value);
    onChange(option.value);
    setSearchTerm("");
  };

  const handleMouseEnter = (value: string) => {
    setHoveredOption(value);
  };

  const handleMouseLeave = () => {
    setHoveredOption(undefined);
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const substringLabel = selectedOption
    ? `${selectedOption.label.substring(0, 8)}...`
    : "";

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className={`${styles.selectContainer} ${
        showOption ? "" : styles.currencyLabel
      } ${prefix ? "min-w-20" : ""}`}
      ref={selectMenuRef}
    >
      <div
        role="button"
        tabIndex={0}
        className={`${styles.selectBox}  ${
          hasArrow ? "flex justify-between !w-full !text-start" : ""
        }   !bg-[${customBg}] min-w-[48px] min-h-6 `}
        aria-expanded={dropdownIsOpen}
        aria-haspopup="listbox"
        aria-label="Toggle currency selection"
      >
        <span className={styles.selectedValue}>
          {currency && (
            <span className={`${currency && styles.customSymbolIcon}`}>
              {selectedOption?.symbol}
            </span>
          )}
          {/* { !prefix  ? <span>{currency ? substringLabel : selectedOption?.label}</span> : ""} */}
          {!prefix && !currency ? <span>{selectedOption?.label}</span> : ""}

          {prefix ? (
            <span>{currency ? substringLabel : selectedOption?.value}</span>
          ) : (
            ""
          )}
        </span>
        {hasArrow && (
          <span className={styles.arrow}>
            <DownwardArrow />
          </span>
        )}
      </div>
      {dropdownIsOpen && (
        <React.Fragment>
          <div className="h-5 w-5 border-t border-l border-gray-200 absolute top-[55px] ltr:left-[15px] rtl:right-[15px] bg-white rotate-45"></div>

          <div
            className={`${styles.dropDown} ${
              currency
                ? styles.currency_selector
                : nationality
                ? styles.nationality_selector
                : prefix
                ? styles.prefix_selector
                : ""
            } ${className} ${isCountry ? styles.country : ""}`}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={0}
            aria-label="Select an option"
          >
            {hasSearch && (
              <div className="p-2 sticky top-[-5px] z-10 bg-white">
                <input
                  type="text"
                  ref={inputRef}
                  placeholder="Search..."
                  autoComplete="off nope "
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full  min-w-full p-1 border rounded"
                />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${
                    currency && styles.currency_option
                  } ${
                    hoveredOption === option.value || value === option.value
                      ? styles.hovered
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  ref={(el) => {
                    if (el) {
                      optionRefs.current.set(option.value, el);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${option.label}`}
                >
                  {currency ? (
                    <React.Fragment>
                      <span className={styles.optionSymbol}>
                        {option.symbol}
                      </span>
                      <span className={`${styles.optionLabel} ms-2`}>
                        {option.label}
                      </span>
                    </React.Fragment>
                  ) : (
                    option.label
                  )}
                </div>
              ))
            ) : (
              <div className="px-2 py-1 text-gray-500 text-sm w-full min-w-full">
                No options found
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CustomSelectSearch;
